import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  FormControl,
  List,
  ListSubheader,
  MenuItem,
  TextField,
  Typography,
  createFilterOptions,
  debounce,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";

import { CustomerOrgCompany } from "../../../types/apiTypes";
import {
  CustomerContract,
  customerOrgAndCompAutocomplete,
} from "../../../../features/customer/customerAPI";
import { fetchCustomerOrgContracts } from "../../../../features/customer/customerContractsSlice";
import { useAppDispatch } from "../../../../app/hooks";
import { SelectLight } from "../../styled/styled";
import { AutocompleteType } from "../../../types/autoComplete";

type Props = {
  onChange: Function;
  name: string;
  defaultValue: AutocompleteType;
  resetValues?: boolean;
};

const initCustOrgOptions = [{ id: "", name: "", customerOrganizations: [] }];

export const CustomerContractAutocomplete = ({
  onChange,
  name,
  defaultValue,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [customerOrgCompany, setCustomerOrgCompany] =
    useState<CustomerOrgCompany | null>(null);

  const [customerSearchString, setCustomerSearchString] = useState("");
  const [customerOrgOptions, setCustomerOrgOptions] =
    useState<CustomerOrgCompany[]>(initCustOrgOptions);
  const [customerContractOptions, setCustomerContractOptions] = useState<
    CustomerContract[]
  >([]);

  const [customerContract, setCustomerContract] = useState(defaultValue);

  useEffect(() => {
    setCustomerContract(defaultValue);
  }, [defaultValue]);

  const handleCustomerSearchStringChange = debounce(
    (value: string) => setCustomerSearchString(value),
    500,
  );

  const dispatchCustomerOrgSearch = (searchString: string) => {
    try {
      customerOrgAndCompAutocomplete({ name: searchString }).then((res) => {
        setCustomerOrgOptions(res);
      });
    } catch (e: any) {
      console.error("Failed to fetch CustomerOrganizations: ", e);
    }
  };

  const customerOptions = useMemo(() => {
    return customerOrgOptions.flatMap((item) =>
      item.customerOrganizations.map((org) => ({
        id: item.id,
        name: item.name,
        customerOrganizations: [{ id: org.id, name: org.name }],
      })),
    );
  }, [customerOrgOptions]);

  const selectCustomerOrg = (_: any, newValue: CustomerOrgCompany | null) => {
    setCustomerOrgCompany(newValue);
  };

  const selectCustomerContract = (event: any) => {
    const contractId = event.target.value;
    const value = customerContractOptions.filter(
      (option) => option.id === contractId,
    );
    onChange(name, value[0].id);
  };

  useEffect(() => {
    if (customerSearchString !== "") {
      dispatchCustomerOrgSearch(customerSearchString);
    }
  }, [customerSearchString]);

  useEffect(() => {
    if (!!customerOrgCompany && customerOrgCompany.id !== "") {
      dispatch(
        fetchCustomerOrgContracts(
          customerOrgCompany.customerOrganizations[0].id,
        ),
      ).then((action) => {
        if (isFulfilled(action)) {
          setCustomerContractOptions(action.payload);
        }
      });
    }
  }, [customerOrgCompany, dispatch]);

  return (
    <>
      <Typography>{t("orderQueues:customerOrg")}</Typography>
      <Autocomplete
        value={customerOrgCompany}
        options={customerOptions}
        getOptionLabel={(option) => {
          const custOrg = option.customerOrganizations[0];
          return custOrg ? custOrg.name : "";
        }}
        filterOptions={createFilterOptions({
          stringify: (option) => JSON.stringify(option),
        })}
        groupBy={(option: any) => option.name}
        renderGroup={({ group, children }) => (
          <>
            <ListSubheader
              disableSticky
              sx={{
                color: "#9167CA",
                fontWeight: 600,
                textTransform: "uppercase",
                lineHeight: "20px",
              }}
            >
              <Typography variant="header">{group}</Typography>
            </ListSubheader>
            <List sx={{ paddingLeft: "32px" }}>{children}</List>
          </>
        )}
        renderInput={(params) => (
          <TextField {...params} size="small" required />
        )}
        onChange={(_, newValue: CustomerOrgCompany | null) =>
          selectCustomerOrg(_, newValue)
        }
        onInputChange={(_, newValue: string) => {
          if (!!newValue) {
            handleCustomerSearchStringChange(newValue);
          }
        }}
      />
      <Typography>{t("orderQueues:customerContract")}</Typography>
      <FormControl fullWidth>
        <SelectLight
          size="small"
          value={customerContract}
          onChange={selectCustomerContract}
        >
          {customerContractOptions
            ? customerContractOptions.map((contract) => (
                <MenuItem key={contract.id} value={contract.id}>
                  {contract.name}
                </MenuItem>
              ))
            : null}
        </SelectLight>
      </FormControl>
    </>
  );
};
